// Unpublished Work © 2023-2024 Deere & Company.

import {jsPDF} from 'jspdf';
import {addDataBlock} from 'Utils/pdf';
import _ from 'lodash';
import moment from 'moment';
import {
    getAllWorkItems,
    getPresentationSettings
} from 'Ui/features/onlabor/workboards/presentation/utils/presentation-utils';
import {getMembershipById} from 'Services/membership-service';
import {JOBS} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {getWorkboard} from 'Services/work-board-service';
import {sortJobsBySeq} from 'Utils/workboard-utils';
import {USER} from 'Ui/constants/workboard-constants';
import {
    sortByEmployeeWorkItemsBySeq,
    sortByJobsWorkItemsByUserOrder
} from 'OnLabor/workboard/utils/workboard-sort';

const FONT_SIZE = 12;
const HEADER_FONT_SIZE = 24;
const LABEL_FONT_SIZE = 12;
const TOP_TO_HEADER_GAP = 24;
const HEADER_TO_LABEL_GAP = 4;
const LABEL_TO_CONTENT_GAP = 24;
const PAGE_MARGIN = 15;

function pointsToUnits(pdf, points) {
    return points / pdf.internal.scaleFactor;
}

function createPDF(workboard) {
    const pdf = new jsPDF();

    pdf.setProperties({
        title: `${workboard.name} - ${workboard.startTime}`
    });
    pdf.setFontSize(FONT_SIZE);

    return pdf;
}

function getWorkItemDetails(workItem) {
    const detailItems = [];

    workItem.formattedEquipmentNames?.forEach((formattedEquipmentName) => detailItems.push(formattedEquipmentName));

    if (workItem.route) {
        detailItems.push(workItem.route);
    }

    if (workItem.note) {
        detailItems.push(workItem.note);
    }

    return detailItems;
}

function getFormattedHeaderDateTime(startTime) {
    const workboardDate = moment(startTime, 'YYYY-MM-DD HH:mm:ss');

    return {
        formattedDate: workboardDate.format('MMM DD, YYYY'),
        formattedTime: workboardDate.format('h:mm A')
    };
}

function addWorkboardHeader(pdf, currentPosition, workboard) {
    const {
        formattedDate,
        formattedTime
    } = getFormattedHeaderDateTime(workboard.startTime);
    const {
        fontName,
        fontStyle
    } = pdf.getFont();
    const headerText = workboard.name || '';
    const labelText = `${formattedDate} | ${formattedTime}`;

    pdf.setFont(fontName, fontStyle, 'bold');
    pdf.setFontSize(HEADER_FONT_SIZE);
    const headerTextHeight = pdf.getTextDimensions(headerText).h;

    currentPosition.y = pointsToUnits(pdf, TOP_TO_HEADER_GAP) + headerTextHeight;
    pdf.text(headerText, currentPosition.x, currentPosition.y);

    pdf.setFont(fontName, fontStyle, 'normal');
    pdf.setFontSize(LABEL_FONT_SIZE);
    const labelTextHeight = pdf.getTextDimensions(labelText).h;

    currentPosition.y += pointsToUnits(pdf, HEADER_TO_LABEL_GAP) + labelTextHeight;

    pdf.text(labelText, currentPosition.x, currentPosition.y);
    currentPosition.y += pointsToUnits(pdf, LABEL_TO_CONTENT_GAP);

    pdf.setFontSize(FONT_SIZE);
}

function formatPDFByJob(pdf, currentPosition, workItems, translations, userOrder, creationFlow) {
    let workItemsByJob = Object.values(_.groupBy(workItems, 'jobId'));

    if (creationFlow === USER) {
        workItemsByJob = sortByJobsWorkItemsByUserOrder(workItems, userOrder);
    }

    workItemsByJob.forEach((workItemsFromJob) => {
        const data = workItemsFromJob.map((workItem) => {
            const estHours = workItem.estHours ? ` ${workItem.estHours}` : '';

            return [
                `${workItem.appUserName}${estHours}`,
                ...getWorkItemDetails(workItem)
            ];
        });

        addDataBlock(pdf, currentPosition, {
            data,
            header: workItemsFromJob[0].title,
            secondaryHeader: workItemsFromJob[0].secondaryName ? `(${workItemsFromJob[0].secondaryName})` : null,
            translations
        });
    });
}

function formatPDFByEmployee(pdf, currentPosition, workItems, translations, userOrder, creationFlow) {
    let workItemsByEmployee = Object.values(_.groupBy(workItems, 'appUserId'));

    if (creationFlow === USER) {
        workItemsByEmployee = sortByEmployeeWorkItemsBySeq(workItemsByEmployee, userOrder);
    }

    workItemsByEmployee.forEach((workItemsFromEmployee) => {
        const data = workItemsFromEmployee.map((workItem) => {
            const estHours = workItem.estHours ? ` ${workItem.estHours}` : '';

            const items = [`${workItem.title}${estHours}`];

            if (workItem.secondaryName) {
                items.push(workItem.secondaryName);
            }

            return [
                ...items,
                ...getWorkItemDetails(workItem)
            ];
        });

        addDataBlock(pdf, currentPosition, {
            data,
            header: workItemsFromEmployee[0].appUserName,
            translations
        });
    });
}

async function generateWorkboardPdf(workboardId, membershipId, translations, teamMembers, orgId) {
    const [{
        workboard, userOrder
    }, {membership}] = await Promise.all([
        getWorkboard(workboardId),
        getMembershipById(membershipId)
    ]);

    const jobs = sortJobsBySeq(workboard.jobs);

    const pdf = createPDF(workboard);

    const currentPosition = {
        x: PAGE_MARGIN,
        y: PAGE_MARGIN
    };
    const presentationSettings = getPresentationSettings(membership);

    addWorkboardHeader(pdf, currentPosition, workboard);

    // PDF output does not render profile pictures, so skip the per-operator image fetches.
    const workItems = await getAllWorkItems(jobs, translations, {
        ...presentationSettings,
        showEmployeeImage: false
    }, teamMembers, orgId);

    if (presentationSettings.grouping === JOBS) {
        formatPDFByJob(pdf, currentPosition, workItems, translations, userOrder, workboard.creationFlow);
    } else {
        formatPDFByEmployee(pdf, currentPosition, workItems, translations, userOrder, workboard.creationFlow);
    }

    return pdf;
}

export {
    generateWorkboardPdf
};
